<template>
    <div id="chat-window-wrapper" ref="chatWindow">
        <Chat
            ref="chat"
            :contact="contact"
            :messageSyncRunning="messageSyncRunning"
            :messages="getMessages()"
            :visitorTypingMessage="visitorTypingMessage"
            :is-live="isLive"
            :showHeader="showHeader"
        />
    </div>
</template>

<script>
import Chat from "./Chat.vue";
import UserInfoWidget from "./UserInfoWidget.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import SYSTEM_PUBSUB, { systemMessageStream } from "../../../utils/systemPubSub";
import { sendSocketMessage, socketMessageStream } from "../../../utils/socketStream";
import { toNewMessagesModel } from "../../../utils/chat";
import { log2 } from "../../../utils/log2";
import { filter, map } from "rxjs/operators";
import _ from "lodash";
import GridChatWindowManager from "./GridChatWindowManager.vue";

const log = log2("ChatWindow");
export default {
    name: "ChatWindow",
    components: { GridChatWindowManager, UserInfoWidget, Chat },
    props: {
        isLive: {
            type: Boolean,
            default: false,
        },
        contact: {
            type: Object,
            default: () => ({}),
        },
        showHeader: {
            type: Boolean,
            default: true,
        },
    },
    data: () => ({
        cancels: [],
        messageSyncRunning: true,
        messages: [],
        lastReSyncIntervalId: null,
        renderCount: 0,
        visitorTypingMessage: null,
    }),
    computed: {
        ...mapState({
            settings: (state) => state.setting.settings,
            chatHistory: (state) => state.chat.chatHistory,
            site: (state) => state.site.site,
            user: (state) => state.auth.user,
        }),
        ...mapGetters({
            getActiveConversationPendingMessages: "chat/getActiveConversationPendingMessages",
            getUser: "user/getUser", // bu kısımı asla kaldırma!
        }),
    },
    mounted() {
        this.initConversation();
        const stream = socketMessageStream
            .pipe(filter(({ cmd }) => cmd === "whatsapp_contact_status"))
            .subscribe(({ data }) => {
                this.setWhatsappContactStatus({
                    whatsappNumber: data.whatsappNumber,
                    status: data.isActive,
                    channelId: data.channelId,
                });
            });

        if (this.contact.whatsappNumber) {
            sendSocketMessage("user whatsapp-contact-status", {
                siteId: this.site._id,
                channelId: this.contact.channel,
                whatsappNumber: this.contact.whatsappNumber,
            });
        }

        this.cancels.push(() => {
            stream.unsubscribe();
        });
    },
    methods: {
        ...mapActions({
            resetChatHistory: "chat/resetChatHistory",
            loadOldHistoryMessages: "chat/loadOldHistoryMessages",
            listContactMessages: "chat/listContactMessages",
            loadLastNMessage: "chat/loadLastNMessage",
            setWhatsappContactStatus: "chat/setWhatsappContactStatus",
            setConversationHistoryMessages: "chat/setConversationHistoryMessages",
        }),
        initConversation() {
            if (this.contact.lastConversation) {
                this.visitorTypingMessage = null;
                this.syncMessages(this.contact.lastConversation._id, this.contact?._id, false, true);
                this.setupResyncMessageWorker(this.contact.lastConversation._id);
                this.initListeners();
            }
        },
        initListeners() {
            const conversationId = this.$_.get(this.contact, "lastConversation._id");
            const contactId = this.$_.get(this.contact, "_id");
            if (!conversationId) return;
            if (!contactId) return;
            this.cancels.forEach((cb) => cb());
            const loadMoreSubscriber = SYSTEM_PUBSUB.stream
                .pipe(filter((x) => x.type === SYSTEM_PUBSUB.MESSAGE_TYPES.ACTIVE_CONVERSATION_LOAD_MORE))
                .subscribe(async () => {
                    this.syncMessages(conversationId, contactId, true).finally(() => {
                        try {
                            document.getElementsByClassName("sc-message-list")[0].scrollTop = 0;
                        } catch (e) {
                            log.error("scroll error", e);
                        }
                    });
                });

            let translateSub = systemMessageStream
                .pipe(
                    filter((x) => x.type === "translate_message"),
                    map((x) => x.payload)
                )
                .subscribe((x) => {
                    this.messages = this.messages.map((msg) => {
                        if (msg.uniqMessageId === x.uniqMessageId) {
                            msg.translates = [...msg.translates, ...x.translates];
                        }
                        return msg;
                    });
                    this.setConversationHistoryMessages({
                        contactId: this.contact._id,
                        conversationId: this.contact.lastConversation._id,
                        messages: this.messages,
                    });
                });

            let messageUpdateSub = socketMessageStream
                .pipe(filter((x) => ["message_updated", "message_deleted", "message_updated_partial"].includes(x.cmd)))
                .subscribe((x) => {
                    const message = x.data;
                    this.messages = this.messages.map((msg) => {
                        if (msg.uniqMessageId === message.uniqMessageId) {
                            if (x.cmd === "message_updated") {
                                return message;
                            } else if (x.cmd === "message_deleted") {
                                x.isDeleted = true;
                                return _.cloneDeep(x);
                            } else if (x.cmd === "message_updated_partial") {
                                console.log("message_updated_partial", { msg, message });
                                return { ...msg, ...message.updatedFields };
                            }
                        }
                        return msg;
                    });
                    this.setConversationHistoryMessages({
                        contactId: this.contact._id,
                        conversationId: this.contact.lastConversation._id,
                        messages: this.messages,
                    });
                });

            const translateMessage = socketMessageStream
                .pipe(filter((x) => ["message"].includes(x.cmd)))
                .subscribe(({ data }) => {
                    if (this.contact.lastConversation && this.contact?.lastConversation?._id !== data.conversationId) {
                        return;
                    }
                    if (data.type === "customer-typing") {
                        if (this.settings.writing === 1) {
                            this.visitorTypingMessage = data.message;
                        }
                        return;
                    } else if (
                        data.messageType === "visitor" &&
                        data.visitorId === this.visitorTypingMessage?.visitorId
                    ) {
                        this.visitorTypingMessage = null;
                    }
                    this.addMultiMessage([data]);
                });

            this.cancels.push(() => {
                translateSub.unsubscribe();
                translateMessage.unsubscribe();
                messageUpdateSub.unsubscribe();
                loadMoreSubscriber.unsubscribe();
            });
        },
        getMessages() {
            let pendingMessages = this.getActiveConversationPendingMessages;
            let allMessages = toNewMessagesModel(this.messages.concat(pendingMessages), this);
            if (this.visitorTypingMessage) {
                allMessages = [
                    ...allMessages,
                    {
                        type: "typing",
                        data: {
                            text: this.visitorTypingMessage,
                        },
                    },
                ];
            }
            let extendedMessageList = [];
            let isHumanMessage = (messageType) =>
                [
                    "user",
                    "user-media",
                    "user-location",
                    "visitor",
                    "visitor-location",
                    "visitor-media",
                    "whatsapp-user",
                    "whatsapp-user-media",
                    "whatsapp-user-location",
                    "instagram-user",
                    "instagram-user-media",
                    "instagram-user-location",
                ].includes(messageType);
            let lastHumanMessage = null;
            for (let i = 0; i < allMessages.length; i++) {
                const current = allMessages[i];
                const messageType = this.$_.get(current, "raw.messageType", null);
                extendedMessageList.push(current);
                if (isHumanMessage(messageType)) lastHumanMessage = current;
            }
            if (this.contact?.lastConversation?.inactive) {
                const startTS = this.$moment(
                    _.get(lastHumanMessage, "raw.createdDate", this.contact?.lastConversation?.createdDate)
                ).valueOf();
                const tookTimeInSeconds = this.$moment().diff(this.$moment(startTS), "seconds");
                const thresholdAsSeconds = (this.settings.inactiveThresholdTime || 5) * 60;
                const isIdle = tookTimeInSeconds >= thresholdAsSeconds * 0.95;
                if (isIdle)
                    extendedMessageList.push({
                        type: "idle-time",
                        data: {
                            startTimestamp: startTS,
                            isLive: true,
                        },
                    });
            }
            log("extendedMessageList", extendedMessageList);
            return this.$_.orderBy(extendedMessageList, ["raw.createdDate"], ["asc"]);
        },
        addMultiMessage(messages) {
            let items = [...this.messages, ...messages];
            items = this.$_.orderBy(
                this.$_.uniqBy(items, (q) => q.uniqMessageId || q._id),
                ["createdDate"],
                ["asc"]
            );
            this.messages = items;
        },
        setMessages(conversationId, newMessageList, resyncMode = false) {
            const activeConversationId = _.get(this.contact, "lastConversation._id");
            if (activeConversationId !== conversationId) {
                log.warn("syncMessages conversationId changed", {
                    conversationId,
                    newConversationId: _.get(this.contact, "lastConversation._id"),
                    resyncMode,
                });
                return;
            }
            const addMessages = () => {
                this.messages = [];
                this.addMultiMessage([
                    {
                        messageType: "load-more",
                        conversationId: conversationId,
                        createdDate: new Date(-8640000000000000),
                        uniqMessageId: "load-more",
                    },
                    ...newMessageList,
                ]);
            };
            if (resyncMode) {
                const diffList = _.differenceBy(newMessageList, this.messages, "uniqMessageId");
                if (diffList.length > 0) addMessages();
                return;
            }
            addMessages();
        },
        async syncMessages(conversationId, contactId, resyncMode = false, cache = false) {
            const activeConversationId = this.$_.get(this.contact, "lastConversation._id");
            log("syncMessages", activeConversationId);
            conversationId = conversationId || activeConversationId;
            if (conversationId !== activeConversationId) {
                log.warn("syncMessages activeConversationId changed", { conversationId, activeConversationId });
                return;
            }
            if (conversationId) {
                if (!resyncMode) this.messageSyncRunning = true;
                return this.listContactMessages({ contactId, conversationId, cache })
                    .then((newMessageList) => {
                        this.setMessages(conversationId, newMessageList, resyncMode);
                    })
                    .finally(() => {
                        if (!resyncMode) this.messageSyncRunning = false;
                    });
            }
        },
        setupResyncMessageWorker(conversationId) {
            if (this.lastReSyncIntervalId) clearInterval(this.lastReSyncIntervalId);
            const sync = async () => {
                const contactId = _.get(this.contact, "_id");
                const messages = await this.loadLastNMessage({
                    contactId,
                    conversationId,
                    take: 50,
                }).catch(() => []);
                const currentChatId = _.get(this.contact, "lastConversation._id");

                if (currentChatId === conversationId) {
                    const diffList = _.differenceBy(messages, this.messages, "uniqMessageId");
                    if (diffList.length > 0) {
                        log.warn("chatwindow messages are not sync", {
                            newMessageList: messages,
                            oldMessageList: this.messages,
                            diffList,
                        });
                        this.addMultiMessage(messages);
                    } else {
                        log("chatwindow messages are sync", {
                            newMessageList: messages,
                            oldMessageList: this.messages,
                            diffList,
                        });
                    }
                }
                this.setMessages(conversationId, messages, true);
                this.renderCount += 1;
            };
            sync();
            const intervalId = setInterval(sync, 3000);
            if (this.lastReSyncIntervalId) clearInterval(this.lastReSyncIntervalId);
            this.lastReSyncIntervalId = intervalId;
            this.cancels.push(() => clearInterval(intervalId));
        },
    },
    beforeDestroy() {
        this.cancels.forEach((fn) => fn());
    },
    watch: {
        contact: {
            deep: true,
            handler(newVal, oldVal) {
                if (newVal?.lastConversation?._id !== oldVal?.lastConversation?._id) this.initConversation();
            },
        },
        socketConnected(isConnected) {
            if (isConnected) {
                if (this.contact) {
                    this.resetChatHistory();
                    this.syncMessages(this.contact?.lastConversation?._id, this.contact?._id);
                }
            }
        },
        chatHistory: {
            handler(newVal) {
                const conversationHistory = newVal[this.contact?.lastConversation?._id];
                if (conversationHistory && conversationHistory.data) {
                    this.setMessages(this.contact?.lastConversation?._id, conversationHistory.data, true);
                }
            },
            deep: true,
        },
    },
};
</script>
<style lang="scss" scoped>
#chat-window-wrapper {
    height: 100%;
    width: 100%;
}
</style>
